// The unit board's own API surface, reached through a share link.
//
// Kept apart from lib/api.ts because none of it belongs to the forecast app:
// it is the same public, unauthenticated MU endpoints the extension panel
// calls, and the token in the URL only says which unit the browser is looking
// at. Holding the link is the whole claim, which is what the unit's channel
// already relies on.

import type { ApiResponse, MuFeedContract, MuState } from "@shared/types";

export interface MuLink {
  token: string;
  muId: string;
}

/** A queue row that arrived through the board.
 *
 * Mirrors `BOARD_ADDED_BY` in the worker's mu-store, and it has to be neither
 * null nor the joiner's own id: `dropStale` deletes self-queued members the
 * GAME roster says are offline, null is already read as self-queued, and using
 * the board does not make anybody online in the game. Sending the joiner's id
 * here is what silently deleted web-only members thirty minutes after they
 * committed. */
export const BOARD_ORIGIN = "board";

/**
 * What a board URL says, and how much of it can be trusted.
 *
 * New links carry the unit id ahead of the token, so a first visit paints real
 * numbers in one round trip instead of resolving the link and then asking for
 * state. Links already pasted into unit channels carry only the token and keep
 * resolving exactly as they did.
 *
 * The unit id is a hint, not an authorisation: the token is still checked, in
 * parallel, and a revoked link still goes dead. Skipping that check would make
 * "replace this link" stop revoking anything, which is the one destructive
 * action in the product.
 */
export function readBoardPath(param: string): { muId: string | null; token: string } {
  const match = /^([0-9a-f]{24})\.(.+)$/i.exec(param);
  return match ? { muId: match[1], token: match[2] } : { muId: null, token: param };
}

/** The URL to paste in a channel. Always the new form: the old one still
 * resolves, but nothing should be minting it. */
export function boardUrl(origin: string, link: MuLink): string {
  return `${origin}/mu/${link.muId}.${link.token}`;
}

async function call<T>(url: string, method = "GET", body?: unknown): Promise<T> {
  const init: RequestInit = { method };
  if (body !== undefined) {
    init.headers = { "Content-Type": "application/json" };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(url, init);
  const json = (await res.json()) as ApiResponse<T>;
  if (!json.success) throw new Error(json.error);
  return json.data;
}

export const muApi = {
  resolveLink: (token: string) => call<MuLink>(`/api/mu/link/${encodeURIComponent(token)}`),
  state: (muId: string) => call<MuState>(`/api/mu/${muId}/state`),
  // What the unit has fought, as the game itself recorded it: who dealt what,
  // and what each of them was paid for it.
  contracts: (muId: string, limit = 10) =>
    call<{ contracts: MuFeedContract[] }>(`/api/mu/${muId}/contracts?limit=${limit}`),
  join: (muId: string, userId: string, username: string, pledgedDamage: number | null) =>
    call<{ ok: true }>(`/api/mu/${muId}/queue`, "POST", {
      userId,
      username,
      pledgedDamage,
      // Not the joiner's id: see BOARD_ORIGIN. Game-offline is not evidence
      // about somebody working the board from a phone.
      addedBy: BOARD_ORIGIN,
    }),
  // `by` names whoever is doing the taking-off, so a member who was away can
  // be told who did it rather than finding themselves silently missing.
  leave: (muId: string, userId: string, by: string | null) =>
    call<{ ok: true }>(
      `/api/mu/${muId}/queue/${userId}${by ? `?by=${encodeURIComponent(by)}` : ""}`,
      "DELETE",
    ),
  setContract: (
    muId: string,
    userId: string,
    battleId: string,
    targetDamage: number,
    bonusPercent: number | null,
  ) =>
    call<{ ok: true }>(`/api/mu/${muId}/contract`, "PUT", {
      battleId,
      targetDamage,
      userId,
      bonusPercent,
    }),
  clearContract: (muId: string) => call<{ ok: true }>(`/api/mu/${muId}/contract`, "DELETE"),
  /**
   * Point the unit at a battle.
   *
   * Unit state, not a device preference: a reader who moves the unit moves it
   * for everybody. `setBy` is attribution, not authentication — MU writes are
   * open, so this records who claims to have set it and no copy built on it may
   * imply it was proved. Only an explicit pick calls this; page reading never
   * does (spec ruling 7).
   */
  setTarget: (muId: string, battleId: string, setBy: string | null) =>
    call<{ stored: boolean; reason: string | null }>(`/api/mu/${muId}/target`, "POST", {
      battleId,
      setBy,
      source: "pick",
    }),
  /** The figure the unit judges itself against while it holds no contract, for
   * one battle. Typed only: real contract terms come from the game. */
  setHandTarget: (muId: string, battleId: string, expectedDamage: number) =>
    call<{ expectedDamage: number }>(`/api/mu/${muId}/target/hand`, "POST", {
      battleId,
      expectedDamage,
    }),
  // The battle's multiplier, kept apart from the contract it is stored on: it
  // describes the battle, and setting it must never restamp a contract the game
  // named as one somebody typed.
  setBattleBonus: (muId: string, bonusPercent: number | null, userId: string | null = null) =>
    call<{ bonusPercent: number | null }>(`/api/mu/${muId}/battle-bonus`, "PUT", {
      bonusPercent,
      // Who set it, so the next person to change it is told whose figure they
      // are replacing rather than a bare "Are you sure?".
      userId,
    }),
  // The unit's board link. `rotate` mints a new one and kills the old, which is
  // the only way a link ever stops working — including for the page doing it.
  link: (muId: string, rotate = false) =>
    call<MuLink>(`/api/mu/${muId}/link`, "POST", { rotate }),
  // An empty string clears the mapping, which is how a wrong id gets fixed.
  setDiscordId: (muId: string, userId: string, discordId: string) =>
    call<{ discordId: string | null }>(`/api/mu/${muId}/discord-id`, "PUT", { userId, discordId }),
};
